import { Injectable, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ViewportService } from '@core/services/viewport.service';

/**
 * Route data flag opting a lazy route into mobile tab preloading.
 * Set on the mobile tabs shell and its five tab child routes in `app.routes.ts`.
 */
export const MOBILE_TAB_PRELOAD_FLAG = 'preloadOnMobile';

/**
 * Preloads the lazy chunks of the mobile tab pages (aufgaben, kanban,
 * diskussionen, notifications, mehr) so the first tap on a tab does not have
 * to download its chunk over the network first (the blank/skeleton flash).
 *
 * Why a custom strategy instead of `PreloadAllModules`:
 * - Desktop keeps zero preloading (unchanged behaviour): only routes flagged
 *   with `preloadOnMobile` are considered, and they are additionally gated on
 *   `ViewportService.isMobile()`.
 * - Angular's `RouterPreloader` starts after the initial navigation finished,
 *   so the initial page load is never competing with the preloads.
 * - Each route is handed to `load()` at most once (idempotency guard here,
 *   plus the router itself skips routes whose component is already loaded).
 *
 * Preload failures (offline, flaky network) are swallowed: preloading is a
 * pure optimization — the affected tab simply lazy-loads on first tap again.
 */
@Injectable({ providedIn: 'root' })
export class MobileTabPreloader implements PreloadingStrategy {
  private readonly viewport = inject(ViewportService);

  /** Route configs already handed to `load()` — idempotency guard. */
  private readonly preloaded = new Set<Route>();

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.[MOBILE_TAB_PRELOAD_FLAG] !== true) {
      return of(null);
    }
    if (!this.viewport.isMobile()) {
      return of(null);
    }
    if (this.preloaded.has(route)) {
      return of(null);
    }
    this.preloaded.add(route);
    return load().pipe(catchError(() => of(null)));
  }
}
